import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DataService } from "./data.service";
import { Observable } from "rxjs";
import {
  PatientEditInfo,
  PatientSearch,
  PatientView,
  SexFilter,
  DocumentTypeFilter,
} from "../model/patient";
import { RelevancePatientFilter } from "../model/patient/relevance-patient-filter";
import { ReservationSearchItem } from "../model/reservation-search-item";
import { PatientCrmSearchItem } from "../model/patient/patient-crm-search-item";
import { PatientReservationSector } from "../model/patient/patient-reservation-sector";
import { NotificationToastService } from "./notification-toast.service";
import { PatientQrInfo } from "../model/patient/patient-qr-info";
import { PatientMail } from "../model/patient/patient-mail";

@Injectable()
export class PatientService {
  constructor(
    private http: HttpClient,
    private notificationToastService: NotificationToastService,
    private dataService: DataService,
  ) {}

  private readonly getPatientReservationSectorNotInOrientationEndPoint: string =
    "/patient/getPatientReservationSectorNotInOrientation/";
  private readonly getPatientsFromSearchEndPoint: string =
    "/patient/getPatientsFromSearch/";
  private readonly getPatientsByParametersEndPoint: string =
    "/patient/getPatientsByParameters/";
  private readonly getPatientViewEndPoint: string = "/patient/getPatientView/";
  private readonly getPatientEditInfoEndPoint: string =
    "/patient/getPatientEditInfo/";
  private readonly getPatientQrInfoEndPoint: string =
    "/patient/getPatientQrInfo/";
  private readonly getSexFiltersEndPoint: string = "/patient/getSexFilters/";
  private readonly getDocumentTypeFiltersEndPoint: string =
    "/patient/getDocumentTypeFilters/";
  private readonly getRelevancePatientFiltersEndPoint: string =
    "/patient/getRelevancePatientFilters/";
  private readonly patientUpdateEndPoint: string = "/patient/patientUpdate/";
  private readonly patientSelectedEndPoint: string =
    "/patient/patientSelected/";
  private readonly getReservationSearchItemsEndPoint: string =
    "/patient/getReservationSearchItems/";
  private readonly getPatientCrmSearchItemsEndPoint: string =
    "/patient/getPatientCrmSearchItems/";
  private readonly patientDisabledEnabledEndPoint: string =
    "/patient/patientDisabledEnabled/";
  private readonly getPatientMailEndPoint: string = "/patient/getPatientMail/";
  private readonly getPatientMailByAtentionEndPoint: string =
    "/patient/getPatientMailByAtention/";
  private readonly getPatientMailByAppointmentIdEndPoint: string =
    "/patient/getPatientMailByAppointmentId/";
  private readonly updatePatientMailEndPoint: string =
    "/patient/updatePatientMail/";

  private readonly updatePatientCellPhonendPoint: string =
    "/patient/updatePatientCellPhone/";

  async updatePatientCellPhone(
    patientId: number,
    cellPhone: string,
  ): Promise<boolean> {
    const params = { PatientId: patientId, CellPhone: cellPhone };
    return await this.dataService
      .post<boolean>(this.updatePatientCellPhonendPoint, params)
      .toPromise();
  }

  async updatePatientMail(patientId: number, mail: string): Promise<boolean> {
    const params = { PatientId: patientId, Mail: mail };
    return await this.dataService
      .post<boolean>(this.updatePatientMailEndPoint, params)
      .toPromise();
  }

  async getPatientMailByAtention(atention: string): Promise<PatientMail> {
    const params = { atention };
    return await this.dataService
      .get<PatientMail>(this.getPatientMailByAtentionEndPoint, params)
      .toPromise();
  }

  async getPatientMailByAppointmentId(
    appointmentId: number,
  ): Promise<PatientMail> {
    const params = { appointmentId };
    return await this.dataService
      .get<PatientMail>(this.getPatientMailByAppointmentIdEndPoint, params)
      .toPromise();
  }

  async getPatientMail(patientId: number): Promise<PatientMail> {
    const params = { patientId };
    return await this.dataService
      .get<PatientMail>(this.getPatientMailEndPoint, params)
      .toPromise();
  }

  async getPatientsByQr(qrBase64: string): Promise<any> {
    try {
      const params = {
        qrBase64,
      };

      return await this.dataService
        .get<PatientSearch>("/patient/getPatientsByExternalDetails/", params)
        .toPromise();
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Paciente",
      );
      return null;
    }
  }

  async getPatientReservationSectorNotInOrientation(
    orientationId: number,
    patientId: number,
  ): Promise<PatientReservationSector[]> {
    const params = { orientationId: orientationId, patientId: patientId };
    return await this.dataService
      .get<
        PatientReservationSector[]
      >(this.getPatientReservationSectorNotInOrientationEndPoint, params)
      .toPromise();
  }

  async patientSelected(patient: PatientView): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.patientSelectedEndPoint, patient)
      .toPromise();
  }

  async patientUpdate(patientEditInfo: PatientEditInfo): Promise<number> {
    return await this.dataService
      .post<number>(this.patientUpdateEndPoint, patientEditInfo)
      .toPromise();
  }

  async patientEnabled(patientId: number): Promise<boolean> {
    const params = { PatientId: patientId, IsEnabled: true };
    return await this.dataService
      .post<boolean>(this.patientDisabledEnabledEndPoint, params)
      .toPromise();
  }

  async patientDisabled(patientId: number): Promise<boolean> {
    const params = { PatientId: patientId, IsEnabled: false };
    return await this.dataService
      .post<boolean>(this.patientDisabledEnabledEndPoint, params)
      .toPromise();
  }

  async getReservationSearchItems(
    patientId: number,
    showPreAdmissionPatientAppointment: boolean = false,
    datesAfter: number = 0,
    datesBefore: number = 0,
  ): Promise<ReservationSearchItem[]> {
    const params = {
      patientId: patientId,
      showPreAdmissionPatientAppointment,
      datesAfter,
      datesBefore,
    };
    return await this.dataService
      .get<
        ReservationSearchItem[]
      >(this.getReservationSearchItemsEndPoint, params)
      .toPromise();
  }

  async getRelevancePatientFilters(): Promise<RelevancePatientFilter[]> {
    const params = {};
    return await this.dataService
      .get<
        RelevancePatientFilter[]
      >(this.getRelevancePatientFiltersEndPoint, params)
      .toPromise();
  }

  async getSexFilters(): Promise<SexFilter[]> {
    const params = {};
    return await this.dataService
      .get<SexFilter[]>(this.getSexFiltersEndPoint, params)
      .toPromise();
  }

  async getDocumentTypeFilters(): Promise<DocumentTypeFilter[]> {
    const params = {};
    return await this.dataService
      .get<DocumentTypeFilter[]>(this.getDocumentTypeFiltersEndPoint, params)
      .toPromise();
  }

  async getPatientQrInfo(qr: string): Promise<PatientQrInfo> {
    const params = { qr };
    return await this.dataService
      .get<PatientQrInfo>(this.getPatientQrInfoEndPoint, params)
      .toPromise();
  }

  async getPatientEditInfo(patientId: number): Promise<PatientEditInfo> {
    const params = { patientId: patientId };
    return await this.dataService
      .get<PatientEditInfo>(this.getPatientEditInfoEndPoint, params)
      .toPromise();
  }

  getPatientsFromSearch(filter: string): Observable<PatientSearch[]> {
    if (!filter || filter === "") {
      return new Observable<PatientSearch[]>();
    }

    const params = { filter: filter };
    return this.dataService.get<PatientSearch[]>(
      this.getPatientsFromSearchEndPoint,
      params,
    );
  }

  async getPatientsByParameters(
    document: string,
    lastName: string,
    firstName: string,
  ): Promise<PatientSearch[]> {
    const params = {
      document: document,
      lastName: lastName,
      firstName: firstName,
    };

    return await this.dataService
      .get<PatientSearch[]>(this.getPatientsByParametersEndPoint, params)
      .toPromise();
  }

  async getPatientCrmSearchItems(
    lastName: string,
    firstName: string,
    deleted: boolean,
    number?: number,
    physicianId?: number,
  ): Promise<PatientCrmSearchItem[]> {
    const params = {
      lastName: lastName,
      firstName: firstName,
      deleted: deleted,
      number: number,
      physicianId,
    };

    return await this.dataService
      .get<
        PatientCrmSearchItem[]
      >(this.getPatientCrmSearchItemsEndPoint, params)
      .toPromise();
  }

  async getPatientView(patientId: number): Promise<PatientView> {
    const params = { patientId: patientId };
    return await this.dataService
      .get<PatientView>(this.getPatientViewEndPoint, params)
      .toPromise();
  }
}
